/**
 * Turns the composer's local image previews into real inline parts.
 *
 * While writing, an inserted image is an `<img src="blob:…" data-inline-id="…">`
 * so the writer sees it immediately. A blob URL means nothing outside this tab,
 * so before the message goes out every one of them becomes a `cid:` reference
 * with a matching part — which is exactly how mail has always embedded images,
 * and what makes them appear inline for the recipient rather than as a row of
 * attachments to open one by one.
 */
export interface InlineImage {
  /** The id the editor stamped on the element. */
  id: string;
  blobId: string;
  type: string;
  name: string;
}

export interface PreparedBody {
  html: string;
  /** Parts to send alongside, each referenced by the HTML above. */
  attachments: { blobId: string; type: string; name: string; cid: string; disposition: string }[];
}

/** A Content-ID that is unique per message and valid per RFC 2392. */
function contentId(index: number): string {
  // No randomness beyond the index is needed: ids only have to be unique
  // within one message, and a predictable one is easier to debug.
  return `inline-${index}@syndeo.mail`;
}

export function prepareInlineImages(html: string, available: InlineImage[]): PreparedBody {
  // `data-pending` matters as much as `data-inline-id`: pressing Send while an
  // image is still uploading would otherwise ship the placeholder, which the
  // recipient sees as a broken image.
  if (!html.includes("data-inline-id") && !html.includes("data-pending")) {
    return { html, attachments: [] };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const byId = new Map(available.map((image) => [image.id, image]));
  const attachments: PreparedBody["attachments"] = [];
  const used = new Map<string, string>();

  doc.querySelectorAll<HTMLImageElement>("img[data-inline-id]").forEach((img) => {
    const id = img.dataset.inlineId!;
    const image = byId.get(id);
    if (!image) {
      // Referenced but never uploaded — drop it rather than send a broken
      // reference the recipient's client would render as a missing image.
      img.remove();
      return;
    }

    // The same image used twice should be one part, not two.
    let cid = used.get(id);
    if (!cid) {
      cid = contentId(attachments.length);
      used.set(id, cid);
      attachments.push({
        blobId: image.blobId,
        type: image.type,
        name: image.name,
        cid,
        // `inline` is what tells a client to render it in place rather than
        // list it in the attachment tray.
        disposition: "inline",
      });
    }

    img.setAttribute("src", `cid:${cid}`);
    img.removeAttribute("data-inline-id");
    img.removeAttribute("data-pending");
  });

  // Anything still uploading when Send was pressed has no part to point at.
  doc.querySelectorAll("img[data-pending]").forEach((img) => img.remove());

  return { html: doc.body.innerHTML, attachments };
}
